import cx from "components/src/utils/cx";
import { ButtonSizeType, ButtonType, ButtonProps, ButtonVariantType } from "../types";
import styles from "../Button.module.scss";

export const iconOnlyFocusedDestructive = "group-focus-visible:[&>svg_path]:!cui-fill-fg-inverse";

export const statusIcons: { completed: string, alert: string, destructive: string, pending?: string } = {
    completed: "ui_checkmark_circle",
    alert: "ui_warning",
    destructive: "ui_trash"
}

export const BUTTON_SIZES = (size: ButtonSizeType, hasDescription: boolean, type: ButtonType, variant?: ButtonVariantType) => {
  const [isXs, isSm, isMd, isLg, isTertiary, isPillVariant] = [size === "xs", size === "sm", size === "md", size === "lg", type === "tertiary", variant === "pill"];
  const padding = cx({
    "cui-px-xs cui-py-2xs": isXs && !isPillVariant,
    "cui-px-sm cui-py-xs": isSm && !isPillVariant,
    "cui-px-lg cui-py-sm": (isMd || isLg) && !hasDescription && !isPillVariant,
    "cui-px-lg cui-py-2xs": (isMd || isLg) && hasDescription && !isPillVariant,
    "cui-py-2xs cui-px-xs": isXs && isPillVariant,
    "cui-p-xs": isSm && isPillVariant,
    "cui-p-sm": isMd && isPillVariant,
    "cui-p-md": isLg && isPillVariant
  });
  const notTertiaryNotPill = !isTertiary && !isPillVariant;
  const isTertiaryOrPill = isTertiary || isPillVariant;

  const typography = cx({
    "heading-sm-bold": isXs && notTertiaryNotPill,
    "heading-md-bold": isSm && notTertiaryNotPill,
    "heading-lg-bold": isMd && notTertiaryNotPill,
    "heading-xl-bold": isLg && notTertiaryNotPill,
    "heading-sm": (isXs && isTertiary) || (isXs && isPillVariant) || (isSm && isPillVariant),
    "heading-md": (isSm && isTertiary) || (isMd && isPillVariant),
    "heading-lg": (isMd && isTertiary) || (isLg && isPillVariant),
    "heading-xl": isLg && isTertiary,
  });
  const sizes = cx({
    "cui-h-6": isXs && !isPillVariant,
    "cui-h-8": isSm && !isPillVariant,
    "cui-h-11": isMd && !isPillVariant,
    "cui-h-12": isLg && !isPillVariant,
    "cui-min-h-[20px]": isXs && isPillVariant,
    "cui-min-h-[32px]": isSm && isPillVariant,
    "cui-min-h-[44px]": isMd && isPillVariant,
    "cui-min-h-[48px]": isLg && isPillVariant,
  });
  return `${padding} ${sizes} ${typography}`.trim();
};

const iconOnlyClasses = {
  xs: "cui-h-6 cui-min-w-[24px] cui-p-2xs",
  sm: "cui-h-8 cui-min-w-[32px] cui-p-xs",
  md: "cui-h-11 cui-min-w-[44px] cui-p-sm",
  lg: "cui-h-12 cui-min-w-[48px] cui-p-sm",
};

const iconOnlyClassesPill = {
  xs: "cui__pill_variant_sizes cui-h-6 cui-min-w-[24px] cui-p-2xs cui-flex cui-items-center cui-justify-center",
  sm: "cui__pill_variant_sizes cui-h-8 cui-min-w-[32px] cui-p-2xs cui-flex cui-items-center cui-justify-center",
  md: "cui__pill_variant_sizes cui-h-11 cui-min-w-[44px] cui-p-xs cui-flex cui-items-center cui-justify-center",
  lg: "cui__pill_variant_sizes cui-h-12 cui-min-w-[48px] cui-p-sm cui-flex cui-items-center cui-justify-center",
};

export const buttonStyles = ({
    size,
    type,
    fluid = false,
    selected,
    status,
    isDisabled,
    iconOnly,
    hasIcon,
    variant,
    hasRightIcon,
    hasDescription,
  }: Partial<ButtonProps> & { size: ButtonSizeType, type: ButtonType, iconOnly: boolean, hasIcon: boolean, hasRightIcon: boolean, isDisabled: boolean, hasDescription: boolean }) => {

  const hasIcons = hasIcon || hasRightIcon;
  const hasStatus = !!status;
  const hasVariant = !!variant;
  const showGap = (hasIcons || hasStatus) && !iconOnly;
  let activeState = styles[type]; 
  if (hasStatus) activeState = `${styles[`status-${status}`]} ${styles[type]}`;
  if (hasVariant && !hasStatus) activeState = `${styles[variant]} ${styles[type]}`;
  const largeSizes = ["lg", "md"];
  const isPillVariant = variant === "pill";
  const buttonClasses = cx( 
    {
      [activeState]: true,
      [BUTTON_SIZES(size, hasDescription, type, variant)]: !iconOnly,
      [iconOnlyClasses[size]]: iconOnly,
      [iconOnlyClassesPill[size]]: iconOnly && isPillVariant,
      [styles["disabled"]]: isDisabled && status !== "pending",
      [styles["selected"]]: selected && !isDisabled,
      "cui-w-auto": !fluid,
      "cui-w-full": fluid,
    },
    `${styles["cui__button"]}`
  );
  const innerButtonStyles = cx({
    "cui-gap-x-sm": showGap && largeSizes.includes(size),
    "cui-gap-x-2xs": showGap && (size === "sm" || size === "xs"),
    "cui-justify-between": hasRightIcon,
    "cui-justify-center": !hasRightIcon,
    "cui-w-auto": !fluid,
    "cui-w-full": fluid,
  })
  return { buttonClasses, innerButtonStyles };
};
